// frontend/src/modules/workspace/components/ChangeRolesDialog.tsx
//
// Change which roles a member holds (PUT /members/:id/roles). Surfaces the
// backend's 'last_owner' / 'unknown_role' errors as toasts.

import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import { workspaceApi } from '../api';
import type { WorkspaceMember, WorkspaceRole } from '../types';
import { CheckboxRow } from './CheckboxRow';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  member: WorkspaceMember | null;
  roles: WorkspaceRole[];
  onSaved: (updated: WorkspaceMember) => void;
}

export function ChangeRolesDialog({ open, onOpenChange, member, roles, onSaved }: Props) {
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open && member) {
      setSelected(new Set(member.roles.map((r) => r.key)));
    }
  }, [open, member]);

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function save() {
    if (!member || busy) return;
    setBusy(true);
    try {
      const updated = await workspaceApi.setMemberRoles(member.id, Array.from(selected));
      toast.success('Roles updated');
      onSaved(updated);
      onOpenChange(false);
    } catch (err: any) {
      const msg = err?.code === 'last_owner'
        ? 'This workspace must keep at least one owner.'
        : err?.code === 'unknown_role'
          ? 'One of the selected roles no longer exists.'
          : (err.message ?? String(err));
      toast.error('Update failed', { description: msg });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Change roles</DialogTitle>
          <DialogDescription>
            {member ? <>Roles for <span className="font-semibold text-foreground">{member.name || member.email}</span>.</> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 pt-2">
          <Label>Roles</Label>
          <ul className="space-y-2 max-h-72 overflow-y-auto scrollbar-thin pr-0.5">
            {roles.map((r) => (
              <li key={r.id}>
                <CheckboxRow
                  checked={selected.has(r.key)}
                  onToggle={() => toggle(r.key)}
                  title={r.name}
                  subtitle={r.description ?? r.key}
                />
              </li>
            ))}
          </ul>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save roles'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
